import React, {useCallback, useMemo, useState} from "react";
import Button from "../../base/button/button";
import classnames from "classnames";
import SubmitTicketDialog from "../../dialogs/submit-ticket";
import {toast} from "react-toastify";
import {useApplicationToastContainer} from "../toast-container";

/**
 * @param {string} title
 * @param {string} message
 * @param {import('react-toastify').ToastContentProps} toastProps
 * @param {boolean} hideTicketSubmission
 * @param {any[]} buttons
 * @param {any} actions
 * @return {JSX.Element}
 * @constructor
 */
const ToastContent = ({title, message, toastProps, hideTicketSubmission, buttons, actions}) => {
    const [openTicketDialog, setOpenTicketDialog] = useState(false);
    const ticketData = useMemo(() => ({
        curl: toastProps.data?.curl,
        capturedScreen: toastProps.data?.capturedScreen,
        url: window.location.origin + (toastProps.data?.location?.pathname ?? ''),
        apiResponse: toastProps.data?.apiResponse,
        apiRequest: toastProps.data?.apiRequest,
        response: toastProps.data?.response,
    }), [toastProps])

    /**
     * Closes the current toast that this component belongs to as soon as the ticket submission dialog is closed.
     */
    const onDialogClosed = () => {
        setOpenTicketDialog(false);
        toastProps.closeToast();
    }

    const closeButtonProps = useMemo(() => {
        const props = {...toastProps.toastProps ?? {}};
        delete props.key;
        return props;
    }, [toastProps.toastProps])

    return (
        <>
            <div className={'toast-content'}>
                <div className={'toast-content-header'}>
                    <p className={'toast-title'}>
                        {title ?? ''}
                    </p>
                    <CloseButton key={toastProps?.toastProps?.key} {...closeButtonProps} />
                </div>

                {
                    message &&
                    <p className={'toast-message'}>
                        <span dangerouslySetInnerHTML={{__html: message ?? ""}}/>
                    </p>
                }
                <div className={'toast-actions'}>
                    {
                        buttons?.map((e, index, array) => (
                            <Button
                                key={index}
                                {...e}
                                onClick={() => e.onClick(toastProps)}
                                className={classnames(
                                    'font-weight-semi-bold py-1 px-3',
                                    {
                                        'mr-3': (index < array.length && !hideTicketSubmission) || (index < array.length - 1)
                                    },
                                    e.className,
                                )}
                                variant={'outlined'}
                                color={'primary'}
                            />
                        ))
                    }
                    {
                        actions
                    }
                    {
                        !hideTicketSubmission &&
                        <Button
                            className={classnames('font-weight-semi-bold py-1 px-3',)}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => setOpenTicketDialog(true)}
                        >
                            Submit Ticket
                        </Button>
                    }
                </div>
            </div>
            <SubmitTicketDialog
                open={openTicketDialog}
                close={onDialogClosed}
                data={ticketData}
            />
        </>
    )
}


/**
 * @param {import('react-toastify').ToastContentProps['toastProps']} props
 */
const CloseButton = (props) => {
    const activeToastIds = useApplicationToastContainer();
    const showCloseAll = useMemo(() => {
        if (activeToastIds.length <= 1)
            return false;
        const toastId = props.toastId;
        return activeToastIds[activeToastIds.length - 1] === toastId;
    }, [activeToastIds, props.toastId]);

    /**
     * Closes all the active toasts.
     * @type {React.MouseEventHandler<HTMLButtonElement>}
     */
    const closeAllToasts = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        for (let toastId of activeToastIds) {
            toast.dismiss(toastId)
        }
    }, [activeToastIds]);

    return (
        <>
            <div className={'toast-close-buttons-container'}>
                {
                    showCloseAll &&
                    <button onClick={closeAllToasts} className={'Toastify__close-all-button'} type="button" aria-label={'Close all toasts'}>
                        Clear all
                    </button>
                }
                <button
                    className={`Toastify__close-button ${props.theme === 'dark' ? 'Toastify__close-button--dark' : ''}`}
                    type="button"
                    onClick={props.closeToast}
                    aria-label={'close toast'}
                >
                    <svg aria-hidden="true" viewBox="0 0 14 16">
                        <path
                            fillRule="evenodd"
                            d="M7.71 8.23l3.75 3.75-1.48 1.48-3.75-3.75-3.75 3.75L1 11.98l3.75-3.75L1 4.48 2.48 3l3.75 3.75L9.98 3l1.48 1.48-3.75 3.75z"
                        />
                    </svg>
                </button>
            </div>
        </>
    );
}

export default ToastContent;
